import React from "react";
import { Capacity } from "../../../../App";
import { CapacitiesHelper, CapacityNames } from "../../../../helpers/capacities.helper";
import StandardButton, { StandardButtonTheme } from "../../../common/button/StandardButton";
import './CapacityPanelItemModalElements.scoped.scss';
import './CapacityPanelItem.scoped.scss';

enum ExpandedSection {
    none = "NoSectionExpanded",
    keywords = "KeywordsExpanded",
    linkedCapacities = "LinkedCapacitiesExpanded",
    lifeCourse = "LifeCourseExpanded"
}

interface ComponentProps {
    didClickOnLearnMore: (capacity: Capacity) => void;
    presentModalContent: (content: JSX.Element) => void;
    didClickOnCapacity: (capacity: Capacity) => void;
    capacity: Capacity;
    isActive: boolean;
}

interface ComponentState {
    expandedSection: ExpandedSection;
}

class CapacityPanelItem extends React.Component<ComponentProps, ComponentState> {
    constructor(props: ComponentProps) {
        super(props);
        this.state = { expandedSection: ExpandedSection.none };
    }

    shouldComponentUpdate(nextProps: ComponentProps, nextState: any) {
        if (this.props.capacity !== nextProps.capacity) {
            return true;
        }
        if (this.props.isActive !== nextProps.isActive) {
            return true;
        }
        if (this.state.expandedSection !== nextState.expandedSection) {
            return true;
        }
        return false;
    }

    moveBackToMainSection() {
        this.props.didClickOnCapacity(Capacity.General);
    }

    didClickOnLearnMore = () => {
        if (this.props.capacity !== Capacity.General) {
            const contentCapacity = CapacitiesHelper.capacityContentForCapacity(this.props.capacity);
            window.open(contentCapacity.learnMoreLink, '_blank')?.focus();
        } else {
            window.open("https://www.unicef-irc.org/publications/pdf/What-Makes-Me_Core-Capacities-for-Living-and-Learning.pdf#page=17", '_blank')?.focus();
        }
    }

    expandKeywords = () => {
        this.expandSection(ExpandedSection.keywords);
    }

    expandLinkedCapacities = () => {
        this.expandSection(ExpandedSection.linkedCapacities);
    }

    expandLifeCourse = () => {
        this.expandSection(ExpandedSection.lifeCourse);
    }

    expandSection(sectionToExpand: ExpandedSection) {
        if (this.state.expandedSection === sectionToExpand) {
            this.setState({ expandedSection: ExpandedSection.none });
        } else {
            this.setState({ expandedSection: sectionToExpand });
        }
    }

    presentModal = () => {
        const contentCapacity = CapacitiesHelper.capacityContentForCapacity(this.props.capacity);
        console.log(contentCapacity);
        let commonPart: JSX.Element =
            <>
                <div className="panel-section-title categoryPanel">{contentCapacity.name}</div>
                <div className="panel-description mainDescription categoryPanel fullHeight">
                    <span dangerouslySetInnerHTML={{ __html: contentCapacity.description }}></span>
                </div>
            </>
        let specificPart = [];
        if (this.props.capacity !== Capacity.General) {
            const topPart =
                <>
                    <div className="panel-learnMoreWrp">
                        <StandardButton buttonText="Learn More" buttonIconURL="/assets/buttonLearnMoreIcon.svg"
                            buttonIconWidth={9} buttonClickCallback={this.didClickOnLearnMore}
                        ></StandardButton>
                    </div>
                    <div className="separator tabletHidden"></div>
                    <div className="keywords-wrp">
                        <div className="panel-section-title keywordsTitle">
                            Keywords
                        </div>
                        <div className="panel-description keywordsPanel">{contentCapacity.keywords}</div>
                    </div>
                    <div className="separator"></div>
                </>

            const linkedCapacities =
                <>
                    <div className="linkedCapacities-wrp">
                        <div className="panel-section-title linkedCapacitiesTitle">
                            Linked Capacities
                        </div>
                        <div className="capacitiesList-wrp linkedCapacitiesPanel">
                            {
                                contentCapacity.linkedCapacitiesName.map((capacity) => {
                                    return <div key={capacity} className={['capacityKeyword', capacity].join(' ')}>{capacity}</div>
                                })
                            }
                        </div>
                    </div>
                    <div className="separator"></div>
                </>

            const lifeCourse =
                <div className="lifeCourse-wrp">
                    <div className="panel-section-title lifeCourseTitle">
                        Life course / development
                    </div>
                    <div className="panel-description lifeCoursePanel tabletHidden"><b>{contentCapacity.lifeCourseDevelopmentTitle}</b>; {contentCapacity.lifeCourseDevelopmentValue}</div>
                </div>

            specificPart.push(topPart);
            if (contentCapacity.linkedCapacitiesName.length > 0) {
                specificPart.push(linkedCapacities);
            }
            specificPart.push(lifeCourse);
        }
        let modalContent: JSX.Element =
            <div className="modal-content">
                {commonPart}
                {specificPart}
            </div>
        this.props.presentModalContent(modalContent);
    }

    capacityForString = (capacityName: string) => {
        switch (capacityName) {
            case CapacityNames.Reflecting:
                return Capacity.Reflecting;
            case CapacityNames.Listening:
                return Capacity.Listening;
            case CapacityNames.DiscerningPatterns:
                return Capacity.DiscerningPatterns;
            case CapacityNames.Inquiring:
                return Capacity.Inquiring;
            case CapacityNames.Embodying:
                return Capacity.Embodying;
            case CapacityNames.Relaxing:
                return Capacity.Relaxing;
            case CapacityNames.Empathizing:
                return Capacity.Empathizing;
            case CapacityNames.Sensing:
                return Capacity.Sensing;
            case CapacityNames.Observing:
                return Capacity.Observing;
            default:
                alert("Something went wrong with the capacity " + capacityName);
                return Capacity.Reflecting;
        }
    }

    render() {
        let content: JSX.Element = <></>;
        if (this.props.capacity === Capacity.General) {
            content =
                <div className={['panel-innerWrp', this.props.isActive ? 'active' : ''].join(' ')} key={Capacity.General}>
                    <div className="panel-title">What are Core Capacities?</div>
                    <div className="panel-description mainDescription">
                        <img className="mobile-whiteFader" src="/assets/tabletPanelWhiteFaderVerticalBottomToTop.png" alt="White gradient overlay to hide text. Purely visual." />
                        Evidence suggests that developing specific core capacities from childhood can support performance in school, work, and life.<br /><br />These nine “core capacities” are cornerstones of life skills. We often overlook these capacities as innate basic skills, so they are underutilized in efforts to promote child well-being and development.<br /><br />But by nurturing, expanding, and modelling these capacities, children can better understand and interact with the world around them, and realise their unique potential.
                    </div>
                    <div className="panel-learnMoreWrp tabletHidden">
                        <StandardButton buttonText="Learn More" buttonIconURL="/assets/buttonLearnMoreIcon.svg"
                            buttonIconWidth={9} buttonClickCallback={this.didClickOnLearnMore}
                        ></StandardButton>
                    </div>
                    <div className="panel-learnMoreWrp desktopHiddenElement">
                        <StandardButton buttonText="Read more" buttonIconURL="/assets/readMoreIcon.svg"
                            buttonIconWidth={14} buttonClickCallback={this.presentModal} buttonTheme={StandardButtonTheme.whiteBackgroundBlueBorder}
                        ></StandardButton>
                    </div>
                </div>;
        } else {
            const contentCapacity = CapacitiesHelper.capacityContentForCapacity(this.props.capacity);
            let listOfElements = [];
            listOfElements.push(
                <>
                    <div className="panel-title categoryPanel">{contentCapacity.name}</div>
                    <div className="panel-description mainDescription categoryPanel">
                        <img className="mobile-whiteFader" src="/assets/tabletPanelWhiteFaderVerticalBottomToTop.png" alt="White gradient overlay to hide text. Purely visual." />
                        <span dangerouslySetInnerHTML={{ __html: contentCapacity.description }}></span>
                    </div>
                    <div className="panel-learnMoreWrp">
                        <span className="tabletHidden panel-section-tabletReadMore">
                            <StandardButton buttonText="Learn More" buttonIconURL="/assets/buttonLearnMoreIcon.svg"
                                buttonIconWidth={9} buttonClickCallback={this.didClickOnLearnMore}
                            ></StandardButton>
                        </span>
                        <span className="desktopHiddenElement panel-section-tabletReadMore">
                            <StandardButton buttonText="Read more" buttonIconURL="/assets/readMoreIcon.svg"
                                buttonIconWidth={14} buttonClickCallback={this.presentModal}
                                buttonTheme={StandardButtonTheme.whiteBackgroundBlueBorder}
                            ></StandardButton>
                        </span>
                    </div>
                    <div className="separator tabletHidden"></div>
                    <div className="panel-learnMoreWrp">
                        <span className="tabletHidden panel-section-tabletReadMore">
                            <div className="panel-section-backHome">
                                <StandardButton buttonText="What are capacities?" buttonClickCallback={() => { this.moveBackToMainSection() }}
                                    buttonIconURL="/assets/buttonLearnMoreIcon.svg"
                                ></StandardButton>
                            </div>
                        </span>
                    </div>
                    <div className="separator tabletHidden"></div>

                    <div className="keywords-wrp categoryPanel">
                        <div className="panel-section-title keywordsTitle">
                            <div className="actual-section-title">
                                Keywords
                            </div>
                            <div className="expand-icon-wrp keywordsExpand" onClick={this.expandKeywords}>
                                <img src="/assets/expandSectionArrow.svg" className="expand-icon" alt="Expand icon button" />
                            </div>
                            <span className="desktopHiddenElement panel-section-tabletReadMore">
                                <StandardButton buttonText="Read more" buttonIconURL="/assets/readMoreIcon.svg"
                                    buttonIconWidth={14} buttonClickCallback={this.presentModal}
                                    buttonTheme={StandardButtonTheme.whiteBackgroundBlueBorder}
                                ></StandardButton>
                            </span>
                        </div>
                        <div className="panel-description keywordsPanel tabletHidden">{contentCapacity.keywords}</div>
                    </div>

                    <div className="separator tabletHidden"></div>
                </>);


            if (contentCapacity.linkedCapacitiesName.length > 0) {
                listOfElements.push(
                    <>
                        <div className="linkedCapacities-wrp categoryPanel">
                            <div className="panel-section-title linkedCapacitiesTitle">
                                <div className="actual-section-title">
                                    Linked Capacities
                                </div>
                                <div className="expand-icon-wrp linkedCapacitiesExpand" onClick={this.expandLinkedCapacities}>
                                    <img src="/assets/expandSectionArrow.svg" className="expand-icon" alt="Expand icon button" />
                                </div>
                                <span className="desktopHiddenElement panel-section-tabletReadMore">
                                    <StandardButton buttonText="Read more" buttonIconURL="/assets/readMoreIcon.svg"
                                        buttonIconWidth={14} buttonClickCallback={this.presentModal}
                                        buttonTheme={StandardButtonTheme.whiteBackgroundBlueBorder}
                                    ></StandardButton>
                                </span>
                            </div>
                            <div className="capacitiesList-wrp linkedCapacitiesPanel tabletHidden">
                                {
                                    contentCapacity.linkedCapacitiesName.map((capacity) => {
                                        return <div onClick={() => { this.props.didClickOnCapacity(this.capacityForString(capacity)) }} key={capacity} className={['capacityKeyword', capacity].join(' ')}>{capacity}</div>
                                    })
                                }
                            </div>
                        </div>

                        <div className="separator tabletHidden"></div>
                    </>
                )
            }

            listOfElements.push(
                <div className="lifeCourse-wrp categoryPanel">
                    <div className="panel-section-title lifeCourseTitle">
                        <div className="actual-section-title">
                            Life course / development
                        </div>
                        <div className="expand-icon-wrp lifeCourseExpand" onClick={this.expandLifeCourse}>
                            <img src="/assets/expandSectionArrow.svg" className="expand-icon" alt="Expand icon button" />
                        </div>
                        <span className="desktopHiddenElement panel-section-tabletReadMore">
                            <StandardButton buttonText="Read more" buttonIconURL="/assets/readMoreIcon.svg"
                                buttonIconWidth={14} buttonClickCallback={this.presentModal}
                                buttonTheme={StandardButtonTheme.whiteBackgroundBlueBorder}
                            ></StandardButton>
                        </span>
                    </div>
                    <div className="panel-description lifeCoursePanel tabletHidden"><b>{contentCapacity.lifeCourseDevelopmentTitle}</b>: {contentCapacity.lifeCourseDevelopmentValue}</div>
                </div>
            )


            content =
                <div className={['panel-innerWrp', this.props.isActive ? 'active' : '', this.state.expandedSection, contentCapacity.name].join(' ')} key={contentCapacity.name}>
                    {listOfElements}
                </div>
        }

        console.log("Rendering " + this.props.capacity);

        return (
            <>
                {content}
            </>
        )
    }
}

export default CapacityPanelItem;